import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { CalendarIcon } from "@offstreetparking/design-system/icons/CalendarIcon";
import { DateUnavailableIcon } from "@offstreetparking/design-system/icons/DateUnavailableIcon";
import { NoParkingIcon } from "@offstreetparking/design-system/icons/NoParkingIcon";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import type { PermitTypeDto } from "../CreatePermitWizard/types";
import { ParkingUpcoming } from "../dataDisplay/parkingDetails/ParkingUpcoming";
type PermitUnavailableProps = {
	status: PermitTypeDto["status"];
	choices?: PermitTypeDto["wizardSettings"]["choices"];
};
export const PermitTypeUnavailable = ({ status, choices }: PermitUnavailableProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	const errorTitle = (() => {
		switch (status) {
			case "deleted":
				return t`Event Unavailable`;
			case "expired":
				return t`Registration Closed`;
			case "full":
				return t`Parking Full`;
			default:
				return "";
		}
	})();
	const errorMessage = (() => {
		switch (status) {
			case "deleted":
				return t`This event has been canceled or is no longer available.`;
			case "expired":
				return t`This parking option is no longer available.`;
			case "full":
				return t`All permits for this parking option have been reserved.`;
			default:
				return "";
		}
	})();
	const errorIcon =
		status === "deleted" || status === "expired" ? (
			<DateUnavailableIcon className="size-12" />
		) : status === "full" ? (
			<NoParkingIcon className="size-12" />
		) : undefined;
	return (
		<div className="h-full w-full lg:h-auto">
			{status !== "upcoming" ? (
				<div className="space-y-4 pt-8 pb-12">
					<div className="flex flex-col items-center justify-center gap-4 text-center">
						<div className="rounded-lg bg-white p-2">{errorIcon}</div>
						<div className="flex flex-col gap-2">
							<Heading As="h2" className="font-bold text-lg">
								{errorTitle}
							</Heading>
							{errorMessage && <Text intent="muted">{errorMessage}</Text>}
						</div>
					</div>
				</div>
			) : (
				<div className="m-4 flex flex-col space-y-4">
					<Alert
						type="error"
						title={t`Registration for this event is currently unavailable. Please check back after ${formatDate(
							{
								date: choices?.[0]?.rangeStart ?? "",
								formatType: "Abbreviated_DateTime",
								timezone: choices?.[0]?.locations[0]?.timezone,
								language: locale,
							},
						)} to register.`}
					/>

					<div>
						<div className="flex flex-row items-center gap-2">
							<CalendarIcon className="size-4" />
							<Heading As="h2" className="font-bold leading-6">
								{t`Choose When to Park`}
							</Heading>
						</div>

						<Text As="span" size="sm" intent="muted">
							{t`Parking dates and times are shown for each option.`}
						</Text>
					</div>

					<Text As="span" size="md" intent="muted" weight="bold">
						{t`Opening soon...`}
					</Text>

					{choices && choices.length > 0 && choices[0]?.locations[0]?.timezone && (
						<div className="flex w-full max-w-prose flex-col gap-4">
							<ParkingUpcoming parkingOptions={choices} timezone={choices[0].locations[0].timezone} />
						</div>
					)}
				</div>
			)}
		</div>
	);
};
